import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import type { Project } from "@/lib/types";

export function useProjects() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["projects", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .eq("user_id", user.id)
        .order("name");
      if (error) throw error;
      return data as Project[];
    },
    enabled: !!user,
  });
}

// Projects with active item counts
export function useProjectsWithStats() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["projects-with-stats", user?.id],
    queryFn: async () => {
      if (!user) return [];

      // Get active projects
      const { data: projects, error: pErr } = await supabase
        .from("projects")
        .select("*")
        .eq("user_id", user.id)
        .eq("status", "active")
        .order("name");
      if (pErr) throw pErr;

      // Get active item counts per project_tag
      const { data: items, error: iErr } = await supabase
        .from("items")
        .select("project_tag")
        .eq("user_id", user.id)
        .in("status", ["captured", "active"]);
      if (iErr) throw iErr;

      const counts: Record<string, number> = {};
      for (const item of items) {
        if (item.project_tag) {
          counts[item.project_tag] = (counts[item.project_tag] || 0) + 1;
        }
      }

      return (projects as Project[]).map((p) => ({
        ...p,
        activeItemCount: counts[p.name] || counts[p.slug] || 0,
      }));
    },
    enabled: !!user,
  });
}

// Fetch a single project by slug
export function useProjectBySlug(slug: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["project", user?.id, slug],
    queryFn: async () => {
      if (!user || !slug) return null;
      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .eq("user_id", user.id)
        .eq("slug", slug)
        .single();
      if (error) throw error;
      return data as Project;
    },
    enabled: !!user && !!slug,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: {
      name: string;
      slug: string;
      color: string;
      description?: string | null;
      deadline?: string | null;
      url?: string | null;
      parent_id?: string | null;
    }) => {
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase
        .from("projects")
        .insert({ ...input, user_id: user.id })
        .select()
        .single();
      if (error) throw error;
      return data as Project;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["projects-with-stats"] });
    },
  });
}

export type ProjectTreeNode = Project & {
  children: ProjectTreeNode[];
};

export function useProjectTree() {
  const { data: projects, ...rest } = useProjects();

  const tree = useMemo(() => {
    if (!projects) return [];

    const map = new Map<string, ProjectTreeNode>();
    const roots: ProjectTreeNode[] = [];

    // First pass: create nodes
    for (const p of projects) {
      map.set(p.id, { ...p, children: [] });
    }

    // Second pass: build tree
    for (const p of projects) {
      const node = map.get(p.id)!;
      if (p.parent_id && map.has(p.parent_id)) {
        map.get(p.parent_id)!.children.push(node);
      } else {
        roots.push(node);
      }
    }

    return roots;
  }, [projects]);

  return { data: tree, projects, ...rest };
}

export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: Partial<Project> & { id: string }) => {
      const { data, error } = await supabase
        .from("projects")
        .update(updates)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as Project;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["projects-with-stats"] });
    },
  });
}
